import { useMutation } from '@apollo/client'
import { useFormik } from 'formik'
import { isEmpty } from 'lodash'
import * as Yup from 'yup'
import { SignUp, SignUpVariables } from '../../types/operation-types'
import { SIGNUP } from './operations'
import {
  UserFormValues,
  useUserForm,
  getUserGqlCompliant,
  UserFieldName,
} from '../../forms/userForm'
import { useGetUserByToken } from '../userByToken'
import { passwordChecker } from '../../utils/passwordChecker'

const {
  checkLength,
  checkLowercase,
  checkNumber,
  checkUppercase,
  errorLength,
  errorLowercase,
  errorNumber,
  errorUppercase
} = passwordChecker

const ERROR_MESSAGE = 'Please, Agree to Terms and Conditions'

const validationSchemas = {
  passwordValidators: Yup.object({
    password: Yup.string()
      .required('This field is required')
      .matches(checkLowercase, errorLowercase)
      .matches(checkUppercase, errorUppercase)
      .matches(checkNumber, errorNumber)
      .matches(checkLength, errorLength),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords do not match')
      .required('This field is required')
      .matches(checkLowercase, errorLowercase)
      .matches(checkUppercase, errorUppercase)
      .matches(checkNumber, errorNumber)
      .matches(checkLength, errorLength),
    isHIPAAConfirmed: Yup.boolean().oneOf([true], ERROR_MESSAGE),
    isTOSConfirmed: Yup.boolean().oneOf([true], ERROR_MESSAGE),
  }),
  noValidators: Yup.object({
    password: Yup.string().required('This field is required'),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords do not match')
      .required('This field is required'),
    isHIPAAConfirmed: Yup.boolean().oneOf([true], ERROR_MESSAGE),
    isTOSConfirmed: Yup.boolean().oneOf([true], ERROR_MESSAGE),
  })
}

interface SignUpProps {
  token: string
  onCompleted?: () => void
  onError?: () => void
  validationSchema: 'passwordValidators' | 'noValidators'
}

export const useSignUpData = ({ token, onCompleted, onError, validationSchema = 'noValidators' }: SignUpProps) => {
  const [signUp, { loading }] = useMutation<SignUp, SignUpVariables>(SIGNUP)

  const userForm = useUserForm({
    validationSchema: 'fullUser',
    onSubmit: () => {},
  })

  const signUpForm = useFormik({
    initialValues: {
      email: '',
      password: '',
      passwordConfirmation: '',
      isHIPAAConfirmed: false,
      countOfDocuments: 0,
      checkedDocumentIds: [] as string[],
      isTOSConfirmed: false,
      optInMarketing: true,
    },
    validationSchema: validationSchemas[validationSchema],
    async onSubmit() {
      try {
        userForm.handleSubmit()
        const userFormDataError = await userForm.validateForm()
        if (isEmpty(userFormDataError)) {
          await signUp({
            variables: {
              token,
              inputData: {
                password: signUpForm.values.password,
                ...getUserGqlCompliant(userForm.values, [
                  UserFieldName.FIRSTNAME,
                  UserFieldName.LASTNAME,
                  UserFieldName.PHONE_NUMBER,
                  UserFieldName.BIRTHDATE,
                  UserFieldName.GENDER,
                  UserFieldName.ETHNICITY,
                  UserFieldName.RACE,
                  UserFieldName.ADDRESS,
                  UserFieldName.CDC_CARD,
                  UserFieldName.PHOTO_ID,
                  UserFieldName.OPT_IN_MARKETING,
                ]),
              },
            },
          })
          if (onCompleted) {
            onCompleted()
          }
        }
      } catch {
        if (onError) {
          onError()
        }
      }
    },
  })

  const { data: userByTokenData, loading: isUserByTokenDataLoading } = useGetUserByToken({
    token,
    onCompleted: ({ userByToken }) => {
      if (userByToken) {
        void userForm.setValues({
          ...userByToken,
        } as UserFormValues)
        const countOfDocuments = userByToken.PendingGroupInvites.map(
          ({ Documents }) => Documents
        ).reduce((acc, documents) => acc + (documents.length || 1), 0)
        void signUpForm.setFieldValue('countOfDocuments', countOfDocuments)
      }
    },
  })

  return {
    userByTokenData,
    isUserByTokenDataLoading,
    userForm,
    signUpForm,
    isSignUpLoading: loading,
  }
}
